import React, { useState, useEffect, useContext, useRef } from 'react';
import { UserContext } from '../../App';
import {
  Box, Typography, CircularProgress, TextField, TablePagination, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Snackbar, Alert,
  Select, MenuItem, FormControl, LinearProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import * as XLSX from 'xlsx';
import '../HospitalNotices/HospitalNotice.css';
import '../HospitalNotices/HospitalRemainder.css';
import { GreenSwitch } from '../Hospital/HospitalContact';

const PaymentCollections = ({ isSidebarOpen, email }) => {
  const { handleAccess, setAuthPop } = useContext(UserContext);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Group filter state
  const [groupFilter, setGroupFilter] = useState('all');
  const [billingTypeFilter, setBillingTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Sort state
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  
  // Invoice popup state
  const [invoicePopupOpen, setInvoicePopupOpen] = useState(false);
  const [invoiceData, setInvoiceData] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Credit/Debit notes popup state
  const [notesPopupOpen, setNotesPopupOpen] = useState(false);
  const [notesData, setNotesData] = useState([]);
  const [notesType, setNotesType] = useState(''); // 'credit' or 'debit'
  const [adjustmentIndex, setAdjustmentIndex] = useState({}); // reference_no -> invoice numbers
  
  // Reason popup state
  const [reasonPopupOpen, setReasonPopupOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [pendingToggle, setPendingToggle] = useState(null); // Store client info for pending toggle
  
  // Confirmation popup state for enabling
  const [confirmPopupOpen, setConfirmPopupOpen] = useState(false);
  
  // Success/Error message state
  const [alertMessage, setAlertMessage] = useState('');
  const [alertOpen, setAlertOpen] = useState(false);

  // Detailed (client + invoice level) export state
  const [exporting, setExporting] = useState(false);
  const [exportDone, setExportDone] = useState(0);
  const [exportTotal, setExportTotal] = useState(0);
  const [exportCancel, setExportCancel] = useState(false);
  const exportCancelRef = useRef(false);


 console.log('email', email);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);
        const { REACT_APP_GET_CLIENT_MAIL_NOTIFICATION_INSIGHTS } = process.env;
        
        console.log('API URL:', REACT_APP_GET_CLIENT_MAIL_NOTIFICATION_INSIGHTS);
        console.log('newStatus', email);

        
        if (!REACT_APP_GET_CLIENT_MAIL_NOTIFICATION_INSIGHTS) {
          console.error('REACT_APP_GET_CLIENT_MAIL_NOTIFICATION_INSIGHTS is not defined');
          setLoading(false);
          return;
        }
        
        const response = await axios.get(REACT_APP_GET_CLIENT_MAIL_NOTIFICATION_INSIGHTS);
        console.log('API Response:', response);
        
        if (response?.data?.data) {
          setClients(response.data.data);
          setFilteredData(response.data.data);
        } else if (response?.data) {
          setClients(response.data);
          setFilteredData(response.data);
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchClients();
  }, [email]);

  // Filter data when search term, group, billing, or status filter changes
  useEffect(() => {
    let dataToFilter = clients;

    if (groupFilter !== 'all') {
      dataToFilter = dataToFilter.filter(
        (client) => (client.group_name ?? '').toString() === groupFilter
      );
    }

    if (billingTypeFilter !== 'all') {
      dataToFilter = dataToFilter.filter(
        (client) => (client.billing_type ?? '').toString() === billingTypeFilter
      );
    }

    if (statusFilter !== 'all') {
      dataToFilter = dataToFilter.filter(
        (client) => (client.status ?? '').toString() === statusFilter
      );
    }

    if (searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      dataToFilter = dataToFilter.filter(client => {
        return (
          (client.client_id && client.client_id.toString().toLowerCase().includes(lowerSearchTerm)) ||
          (client.client_name && client.client_name.toString().toLowerCase().includes(lowerSearchTerm)) ||
          (client.group_name && client.group_name.toString().toLowerCase().includes(lowerSearchTerm)) ||
          (client.billing_type && client.billing_type.toString().toLowerCase().includes(lowerSearchTerm)) ||
          (client.status && client.status.toString().toLowerCase().includes(lowerSearchTerm))
        );
      });
    }

    setFilteredData(dataToFilter);
    setPage(0);
  }, [searchTerm, clients, groupFilter, billingTypeFilter, statusFilter]);

  // Handle sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sorted data
  const getSortedData = () => {
    if (!sortColumn) return filteredData;

    const sorted = [...filteredData].sort((a, b) => {
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Convert to lowercase for string comparison
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      // Handle numeric values (like due) — without this they sort as strings.
      if (sortColumn === 'due' || sortColumn === 'excess_amount') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      // Handle date values
      if (sortColumn === 'onboarded_at') {
        aValue = new Date(aValue).getTime() || 0;
        bValue = new Date(bValue).getTime() || 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  // Get paginated data
  const getPaginatedData = () => {
    const sortedData = getSortedData();
    return sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  // Date formatter
  const dateBodyTemplate = (rowData) => {
    if (!rowData) return '-';
    const formattedDate = new Date(rowData).toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    return formattedDate;
  };

  // Fetch payment/invoice data
  const fetchPaymentData = async (client) => {
    try {
      setInvoiceLoading(true);
      setSelectedClient(client);
      setInvoicePopupOpen(true);
      
      const { REACT_APP_GET_PAYMENT_DATA } = process.env;
      
      if (!REACT_APP_GET_PAYMENT_DATA) {
        console.error('REACT_APP_GET_PAYMENT_DATA is not defined');
        return;
      }
      
      const response = await axios.get(`${REACT_APP_GET_PAYMENT_DATA}?client_id=${client.client_id}`);
      
      if (response.status === 200) {
        const rawData = response.data?.data || response.data || [];
        const sorted = [...rawData].sort((a, b) => {
          const aDate = a?.invoice_date ? new Date(a.invoice_date).getTime() : 0;
          const bDate = b?.invoice_date ? new Date(b.invoice_date).getTime() : 0;
          return aDate - bDate;
        });
        setInvoiceData(sorted);
      }
    } catch (err) {
      console.error('Failed to fetch payment data:', err);
      setInvoiceData([]);
    } finally {
      setInvoiceLoading(false);
    }
  };

  // Handle row click
  const handleRowClick = (client, e) => {
    // Don't trigger if clicking on the toggle switch
    if (e.target.closest('.MuiSwitch-root')) {
      return;
    }
    fetchPaymentData(client);
  };

  // A single receipt can be split across several invoices: the same
  // reference_no then appears in more than one invoice's payments[]. Count the
  // distinct invoices each reference touches so the popup can say whether a
  // payment was adjusted once or spread over many.
  const buildAdjustmentIndex = (invoices) => {
    const index = {};
    (invoices || []).forEach((inv) => {
      const invoiceNo = inv?.invoice_num;
      (inv?.payments || []).forEach((pay) => {
        const ref = String(pay?.reference_no ?? '').trim();
        if (!ref) return;
        if (!index[ref]) index[ref] = new Set();
        if (invoiceNo) index[ref].add(invoiceNo);
      });
    });
    return Object.fromEntries(
      Object.entries(index).map(([ref, set]) => [ref, [...set]])
    );
  };

  // Handle credit/debit notes click
  const handleNotesClick = (notes, type) => {
    if (!notes || notes.length === 0) {
      return;
    }
    setNotesData(Array.isArray(notes) ? notes : [notes]);
    setNotesType(type);
    setAdjustmentIndex(type === 'payment' ? buildAdjustmentIndex(invoiceData) : {});
    setNotesPopupOpen(true);
  };

  // Close invoice popup
  const handleCloseInvoicePopup = () => {
    setInvoicePopupOpen(false);
    setInvoiceData([]);
    setSelectedClient(null);
  };

  // Close notes popup
  const handleCloseNotesPopup = () => {
    setNotesPopupOpen(false);
    setNotesData([]);
    setNotesType('');
  };

  // Calculate totals
  const handleExportExcel = () => {
    const exportData = getSortedData().map((c) => ({
      'Client ID': c.client_id ?? '-',
      'Client Name': c.client_name ?? '-',
      'Group Name': c.group_name ?? '-',
      'Billing Type': c.billing_type ?? '-',
      'Saas Only': c.saas_only ? 'Yes' : 'No',
      'Onboarded At': c.onboarded_at ? dateBodyTemplate(c.onboarded_at) : '-',
      'Status': c.status ?? '-',
      'Due': c.due != null ? Number(c.due) : 0,
      'Excess': excessOf(c),
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Collections');
    XLSX.writeFile(wb, `Payment_Collections.xlsx`);
  };

  // Fetch invoice/payment rows for a single client (same source as the popup).
  const fetchInvoicesForClient = async (client) => {
    const { REACT_APP_GET_PAYMENT_DATA } = process.env;
    try {
      const res = await axios.get(`${REACT_APP_GET_PAYMENT_DATA}?client_id=${client.client_id}`);
      const raw = res?.data?.data || res?.data || [];
      return Array.isArray(raw) ? raw : [];
    } catch (err) {
      console.error('Failed to fetch invoices for', client.client_id, err);
      return [];
    }
  };

  // Run async workers with a bounded concurrency so we don't fire thousands of
  // requests at once. Stops early if the user cancels.
  const runWithConcurrency = async (items, worker, concurrency, onProgress, shouldStop) => {
    const results = new Array(items.length);
    let cursor = 0;
    let completed = 0;
    const runNext = async () => {
      while (cursor < items.length) {
        if (shouldStop && shouldStop()) return;
        const current = cursor++;
        results[current] = await worker(items[current], current);
        completed += 1;
        if (onProgress) onProgress(completed);
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(concurrency, items.length) }, () => runNext())
    );
    return results;
  };

  // Export client data joined with invoice-level detail into a single sheet.
  const handleExportWithInvoices = async () => {
    const clientsToExport = getSortedData();
    if (!clientsToExport.length) return;

    const { REACT_APP_GET_PAYMENT_DATA } = process.env;
    if (!REACT_APP_GET_PAYMENT_DATA) {
      setAlertMessage('Payment data API is not configured');
      setAlertOpen(true);
      return;
    }

    exportCancelRef.current = false;
    setExportCancel(false);
    setExportDone(0);
    setExportTotal(clientsToExport.length);
    setExporting(true);

    try {
      const invoicesPerClient = await runWithConcurrency(
        clientsToExport,
        fetchInvoicesForClient,
        10,
        (done) => setExportDone(done),
        () => exportCancelRef.current
      );

      if (exportCancelRef.current) {
        setExporting(false);
        return;
      }

      const rows = [];
      clientsToExport.forEach((c, i) => {
        const base = {
          'Client ID': c.client_id ?? '-',
          'Client Name': c.client_name ?? '-',
          'Group Name': c.group_name ?? '-',
          'Billing Type': c.billing_type ?? '-',
          'Saas Only': c.saas_only ? 'Yes' : 'No',
          'Onboarded At': c.onboarded_at ? dateBodyTemplate(c.onboarded_at) : '-',
          'Status': c.status ?? '-',
          'Client Total Due': c.due != null ? Number(c.due) : 0,
          'Client Excess': excessOf(c),
        };
        const invs = [...(invoicesPerClient[i] || [])].sort((a, b) => {
          const aDate = a?.invoice_date ? new Date(a.invoice_date).getTime() : 0;
          const bDate = b?.invoice_date ? new Date(b.invoice_date).getTime() : 0;
          return aDate - bDate;
        });

        if (invs.length === 0) {
          rows.push({
            ...base,
            'Invoice No': '-',
            'Invoice Month': '-',
            'Invoice Amount': 0,
            'Credit': 0,
            'Debit': 0,
            'Payment': 0,
            'Due (Tally)': 0,
            'Due (Actual)': 0,
          });
          return;
        }

        invs.forEach((item) => {
          rows.push({
            ...base,
            'Invoice No': item.invoice_num ?? '-',
            'Invoice Month': item.invoice_date
              ? new Date(item.invoice_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
              : '-',
            'Invoice Amount': Number(item.invoice_amount) || 0,
            'Credit': Number(item.credit_note_amount) || 0,
            'Debit': Number(item.debit_note_amount) || 0,
            'Payment': Number(item.payment_amount) || 0,
            'Due (Tally)': Number(item.due_amount) || 0,
            'Due (Actual)': computeActualDue(item),
          });
        });
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Client + Invoices');
      XLSX.writeFile(wb, 'Payment_Collections_Detailed.xlsx');

      setAlertMessage(`Exported ${rows.length} rows across ${clientsToExport.length} clients`);
      setAlertOpen(true);
      setTimeout(() => setAlertOpen(false), 3000);
    } catch (err) {
      console.error('Detailed export failed:', err);
      setAlertMessage('Failed to export detailed data');
      setAlertOpen(true);
    } finally {
      setExporting(false);
    }
  };

  // TDS deducted at source is never collected from the client — the invoice is
  // settled by payment + TDS together. Counting it as outstanding made a fully
  // paid invoice look unpaid by exactly the TDS amount.
  const tdsOf = (item) => parseFloat(item.tds_amount) || 0;

  // Excess sits on the CLIENT row of the collections API, not on invoices.
  const excessOf = (client) => parseFloat(client?.excess_amount) || 0;

  // Tally's own due for this invoice, net of TDS.
  const dueAfterTds = (item) => {
    const due = (parseFloat(item.due_amount) || 0) - tdsOf(item);
    return due < 0 ? 0 : due;
  };

  const computeActualDue = (item) => {
    if (dueAfterTds(item) <= 0) return 0;
    const inv = parseFloat(item.invoice_amount) || 0;
    const debit = parseFloat(item.debit_note_amount) || 0;
    const credit = parseFloat(item.credit_note_amount) || 0;
    const payment = parseFloat(item.payment_amount) || 0;
    const actual = (inv + debit) - (payment + credit + tdsOf(item));
    return actual < 0 ? 0 : actual;
  };

  // Per reference_no, for the due_bills the API returns alongside each invoice.
  // A bill carries no TDS of its own, so the invoice's TDS is applied to it.
  const billDueAfterTds = (item, bill) => {
    const due = (parseFloat(bill?.due) || 0) - tdsOf(item);
    return due < 0 ? 0 : due;
  };

  const calculateTotals = () => {
    return invoiceData.reduce((acc, item) => {
      return {
        invoice_amount: acc.invoice_amount + (parseFloat(item.invoice_amount) || 0),
        credit_note_amount: acc.credit_note_amount + (parseFloat(item.credit_note_amount) || 0),
        debit_note_amount: acc.debit_note_amount + (parseFloat(item.debit_note_amount) || 0),
        payment_amount: acc.payment_amount + (parseFloat(item.payment_amount) || 0),
        tds_amount: acc.tds_amount + tdsOf(item),
        due_amount: acc.due_amount + dueAfterTds(item),
        actual_due: acc.actual_due + computeActualDue(item),
      };
    }, { invoice_amount: 0, credit_note_amount: 0, debit_note_amount: 0, payment_amount: 0, tds_amount: 0, due_amount: 0, actual_due: 0 });
  };

  // Handle toggle for email notification
  const handleToggle = async (clientId, currentStatus, e) => {
    e.stopPropagation(); // Prevent row click when toggling
    
    // Store pending toggle info
    setPendingToggle({ clientId, currentStatus });
    
    // If disabling (turning off), require a reason
    if (currentStatus === true) {
      setReasonPopupOpen(true);
      return;
    }
    
    // If enabling (turning on), ask for confirmation
    setConfirmPopupOpen(true);
  };

  // Update toggle status with reason
  const updateToggleStatus = async (clientId, currentStatus, disableReason = '') => {
    try {
      const { REACT_APP_UPDATE_DUE_DATA } = process.env;
      
      if (!REACT_APP_UPDATE_DUE_DATA) {
        console.error('REACT_APP_UPDATE_DUE_DATA is not defined');
        return;
      }

      const newStatus = currentStatus ? 2 : 1;
      const payload = {
        client_id: clientId,
        status: newStatus,
        updated_by: email,
      };
      
      // Add reason if disabling
      if (disableReason) {
        payload.reason = disableReason;
      }
      
      const response = await axios.post(REACT_APP_UPDATE_DUE_DATA, payload);

      if (response.status === 200) {
        setClients(prevData =>
          prevData.map(item =>
            item.client_id === clientId
              ? { ...item, automation_restriction: !currentStatus }
              : item
          )
        );
        setFilteredData(prevData =>
          prevData.map(item =>
            item.client_id === clientId
              ? { ...item, automation_restriction: !currentStatus }
              : item
          )
        );
        
        // Show success message
        setAlertMessage('Updated successfully');
        setAlertOpen(true);
        
        // Auto-hide after 3 seconds
        setTimeout(() => {
          setAlertOpen(false);
        }, 3000);
      }
    } catch (err) {
      console.error('Failed to update status:', err);
      setAlertMessage('Failed to update status');
      setAlertOpen(true);
      setTimeout(() => {
        setAlertOpen(false);
      }, 3000);
    }
  };

  // Handle reason submission
  const handleReasonSubmit = () => {
    // Validate reason
    if (!reason.trim()) {
      setReasonError('Reason is required');
      return;
    }
    
    // Check for special characters (allow only letters, numbers, spaces)
    const specialCharPattern = /[^a-zA-Z0-9\s]/;
    if (specialCharPattern.test(reason)) {
      setReasonError('Special characters are not allowed');
      return;
    }
    
    // Proceed with toggle
    if (pendingToggle) {
      updateToggleStatus(pendingToggle.clientId, pendingToggle.currentStatus, reason);
      handleCloseReasonPopup();
    }
  };

  // Close reason popup
  const handleCloseReasonPopup = () => {
    setReasonPopupOpen(false);
    setReason('');
    setReasonError('');
    setPendingToggle(null);
  };

  // Handle enable confirmation
  const handleConfirmEnable = () => {
    if (pendingToggle) {
      updateToggleStatus(pendingToggle.clientId, pendingToggle.currentStatus, '');
      handleCloseConfirmPopup();
    }
  };

  // Close confirmation popup
  const handleCloseConfirmPopup = () => {
    setConfirmPopupOpen(false);
    setPendingToggle(null);
  };

  return (
    <div className={`${isSidebarOpen ? 'notice-main' : 'notice-min'}`}>
      <div className={`${isSidebarOpen ? 'notice-div' : 'notice-div-min'}`}>
        
        {/* Title + Aggregate Section */}
        {(() => {
          const totalClients = filteredData.length;
          const clientsWithDue = filteredData.filter((c) => parseFloat(c.due) > 0).length;
          const totalDue = filteredData.reduce((sum, c) => sum + (parseFloat(c.due) || 0), 0);
          const groupOptions = Array.from(
            new Set(clients.map((c) => c.group_name).filter(Boolean))
          ).sort();
          const billingTypeOptions = Array.from(
            new Set(clients.map((c) => c.billing_type).filter(Boolean))
          ).sort();
          const statusOptions = Array.from(
            new Set(clients.map((c) => c.status).filter(Boolean))
          ).sort();
          return (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0059B2' }}>
                  Collection Details
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: '#eef2ff', borderRadius: '6px', padding: '6px 14px', borderLeft: '3px solid #3b82f6' }}>
                    <Typography sx={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>Total Clients</Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#3b82f6' }}>{totalClients}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: '#fff7ed', borderRadius: '6px', padding: '6px 14px', borderLeft: '3px solid #ea580c' }}>
                    <Typography sx={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>Clients with Due</Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#ea580c' }}>{clientsWithDue}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: '#ecfdf5', borderRadius: '6px', padding: '6px 14px', borderLeft: '3px solid #16a34a' }}>
                    <Typography sx={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>Total Due</Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#16a34a' }}>
                      ₹ {totalDue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Filters Row */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 2, mb: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={groupFilter}
                    onChange={(e) => setGroupFilter(e.target.value)}
                    displayEmpty
                    sx={{ borderRadius: '6px', fontSize: '13px' }}
                  >
                    <MenuItem value="all">All Groups</MenuItem>
                    {groupOptions.map((g) => (
                      <MenuItem key={g} value={g}>{g}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <Select
                    value={billingTypeFilter}
                    onChange={(e) => setBillingTypeFilter(e.target.value)}
                    displayEmpty
                    sx={{ borderRadius: '6px', fontSize: '13px' }}
                  >
                    <MenuItem value="all">All Billing Types</MenuItem>
                    {billingTypeOptions.map((b) => (
                      <MenuItem key={b} value={b}>{b}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    displayEmpty
                    sx={{ borderRadius: '6px', fontSize: '13px' }}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    {statusOptions.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  placeholder="Search"
                  variant="outlined"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ width: 220 }}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    if (handleAccess('PaymentCollection', 'Export')) {
                      handleExportExcel();
                    } else {
                      setAuthPop(true);
                    }
                  }}
                  disabled={filteredData.length === 0 || exporting}
                  sx={{
                    textTransform: 'none',
                    fontSize: '13px',
                    backgroundColor: '#1F2B8F',
                    '&:hover': { backgroundColor: '#172275' },
                  }}
                >
                  Export
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    if (handleAccess('PaymentCollection', 'Export')) {
                      handleExportWithInvoices();
                    } else {
                      setAuthPop(true);
                    }
                  }}
                  disabled={filteredData.length === 0 || exporting}
                  sx={{
                    textTransform: 'none',
                    fontSize: '13px',
                    borderColor: '#1F2B8F',
                    color: '#1F2B8F',
                    '&:hover': { borderColor: '#172275', backgroundColor: '#eef2ff' },
                  }}
                >
                  Export + Invoices
                </Button>
              </Box>
            </>
          );
        })()}

        {/* Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          filteredData.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <Typography variant="body1" color="text.secondary">
                {searchTerm ? 'No results match your search' : 'No data available'}
              </Typography>
            </Box>
          ) : (
            <>
              <div className='email-notification-table'>
                {/* Table Header */}
                <div className='email-notification-table-head' style={{ borderRadius: 0, gridTemplateColumns: '1fr 2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr', backgroundColor: '#1F2B8F' }}>
                  <div onClick={() => handleSort('client_id')} style={{ cursor: 'pointer' }}>
                    Client ID {sortColumn === 'client_id' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                  <div onClick={() => handleSort('client_name')} style={{ cursor: 'pointer' }}>
                    Client Name {sortColumn === 'client_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                  <div onClick={() => handleSort('group_name')} style={{ cursor: 'pointer' }}>
                    Group Name {sortColumn === 'group_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                  <div onClick={() => handleSort('billing_type')} style={{ cursor: 'pointer' }}>
                    Billing Type {sortColumn === 'billing_type' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                  <div onClick={() => handleSort('saas_only')} style={{ cursor: 'pointer' }}>
                    Saas Only {sortColumn === 'saas_only' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                  <div onClick={() => handleSort('onboarded_at')} style={{ cursor: 'pointer' }}>
                    Onboarded At {sortColumn === 'onboarded_at' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                  <div onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                    Status {sortColumn === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                  <div onClick={() => handleSort('due')} style={{ cursor: 'pointer' }}>
                    Due {sortColumn === 'due' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                  <div onClick={() => handleSort('excess_amount')} style={{ cursor: 'pointer' }}>
                    Excess {sortColumn === 'excess_amount' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                </div>

                {/* Table Body */}
                {getPaginatedData().map((client, index) => (
                  <div
                    key={index}
                    className='email-notification-table-body'
                    onClick={(e) => handleRowClick(client, e)}
                    style={{
                      borderRadius: 0,
                      margin: 0,
                      borderTop: index === 0 ? '1px solid lightgray' : 'none',
                      cursor: 'pointer',
                      gridTemplateColumns: '1fr 2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr'
                    }}
                  >
                    <div>{client.client_id || '-'}</div>
                    <div>{client.client_name || '-'}</div>
                    <div>{client.group_name || '-'}</div>
                    <div>{client.billing_type || '-'}</div>
                    <div>{client.saas_only ? 'Yes' : 'No'}</div>
                    <div>{dateBodyTemplate(client.onboarded_at)}</div>
                    <div>{client.status || '-'}</div>
                    <div style={{ color: '#0059B2', fontWeight: 'bold' }}>
                      ₹ {client.due || 0}
                    </div>
                    <div style={{ color: excessOf(client) ? '#15803d' : '#94a3b8', fontWeight: excessOf(client) ? 'bold' : 'normal' }}>
                      {excessOf(client)
                        ? `₹ ${excessOf(client).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                        : '-'}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[10, 15, 25, 50]}
                component="div"
                count={getSortedData().length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )
        )}

        {/* Invoice Details Popup */}
        <Dialog
          open={invoicePopupOpen}
          onClose={handleCloseInvoicePopup}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { maxWidth: '900px' } }}
        >
          <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#1F2B8F', color: '#FFFFFF', padding: '10px 16px' }}>
            <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
              Invoice Details — {selectedClient?.client_name}
            </Typography>
            <IconButton onClick={handleCloseInvoicePopup} size="small" style={{ color: '#FFFFFF' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent style={{ padding: '14px' }}>
            {invoiceLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                <CircularProgress size={24} />
              </Box>
            ) : invoiceData.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                <Typography variant="body1" color="text.secondary">
                  No data available
                </Typography>
              </Box>
            ) : (
              <div style={{ maxHeight: '450px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                {(() => {
                  const gridCols = '140px 70px 1fr 1fr 1fr 1fr 1fr 1fr 1fr';
                  const cellBase = { padding: '6px 8px', textAlign: 'right', fontSize: '12.5px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
                  const leftCell = { ...cellBase, justifyContent: 'flex-start', textAlign: 'left' };
                  const headerCell = { padding: '7px 8px', fontSize: '11px', fontWeight: 700, color: '#fff', textAlign: 'right', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.3px' };
                  const totals = calculateTotals();
                  return (
                    <>
                      <div style={{ position: 'sticky', top: 0, zIndex: 1, display: 'grid', gridTemplateColumns: gridCols, backgroundColor: '#1F2B8F' }}>
                        <div style={{ ...headerCell, textAlign: 'left' }}>Invoice No</div>
                        <div style={{ ...headerCell, textAlign: 'center' }}>Month</div>
                        <div style={headerCell}>Invoice Amt</div>
                        <div style={headerCell}>Credit</div>
                        <div style={headerCell}>Debit</div>
                        <div style={headerCell}>Payment</div>
                        <div style={headerCell}>TDS</div>
                        <div style={headerCell}>Due (Tally)</div>
                        <div style={headerCell}>Due (Actual)</div>
                      </div>

                      {invoiceData.map((item, index) => {
                        const actualDue = computeActualDue(item);
                        return (
                          <div
                            key={index}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: gridCols,
                              borderBottom: '1px solid #f1f5f9',
                              backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa'
                            }}
                          >
                            <div style={leftCell}>{item.invoice_num || '-'}</div>
                            <div style={{ ...cellBase, justifyContent: 'center', textAlign: 'center' }}>
                              {item.invoice_date ? new Date(item.invoice_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '-'}
                            </div>
                            <div style={cellBase}>₹ {parseFloat(item.invoice_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                            <div
                              onClick={() => item.credit_note_amount && handleNotesClick(item.credit_notes, 'credit')}
                              style={{
                                ...cellBase,
                                color: item.credit_note_amount ? '#0059B2' : 'inherit',
                                cursor: item.credit_note_amount ? 'pointer' : 'default',
                                textDecoration: item.credit_note_amount ? 'underline' : 'none',
                                fontWeight: item.credit_note_amount ? 600 : 400,
                              }}
                            >
                              ₹ {parseFloat(item.credit_note_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </div>
                            <div
                              onClick={() => item.debit_note_amount && handleNotesClick(item.debit_notes, 'debit')}
                              style={{
                                ...cellBase,
                                color: item.debit_note_amount ? '#0059B2' : 'inherit',
                                cursor: item.debit_note_amount ? 'pointer' : 'default',
                                textDecoration: item.debit_note_amount ? 'underline' : 'none',
                                fontWeight: item.debit_note_amount ? 600 : 400,
                              }}
                            >
                              ₹ {parseFloat(item.debit_note_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </div>
                            <div
                              onClick={() => item.payment_amount && handleNotesClick(item.payments, 'payment')}
                              style={{
                                ...cellBase,
                                color: item.payment_amount ? '#0059B2' : 'inherit',
                                cursor: item.payment_amount ? 'pointer' : 'default',
                                textDecoration: item.payment_amount ? 'underline' : 'none',
                                fontWeight: item.payment_amount ? 600 : 400,
                              }}
                            >
                              ₹ {parseFloat(item.payment_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </div>
                            <div style={{ ...cellBase, color: tdsOf(item) ? '#7c3aed' : 'inherit' }}>
                              ₹ {tdsOf(item).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </div>
                            <div
                              style={{ ...cellBase, fontWeight: 600, color: '#0059B2' }}
                              title={
                                tdsOf(item)
                                  ? `Tally due ₹${parseFloat(item.due_amount || 0).toLocaleString('en-IN')} less TDS ₹${tdsOf(item).toLocaleString('en-IN')}`
                                  : undefined
                              }
                            >
                              ₹ {dueAfterTds(item).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </div>
                            <div style={{ ...cellBase, fontWeight: 700, color: actualDue > 0 ? '#dc2626' : '#16a34a' }}>
                              ₹ {actualDue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </div>
                          </div>
                        );
                      })}

                      {/* Total Row */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: gridCols,
                        backgroundColor: '#e8f4fd',
                        position: 'sticky',
                        bottom: 0,
                        borderTop: '2px solid #0059B2',
                        fontWeight: 700
                      }}>
                        <div style={{ ...leftCell, gridColumn: 'span 2', justifyContent: 'flex-start' }}>Total</div>
                        <div style={cellBase}>₹ {totals.invoice_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                        <div style={cellBase}>₹ {totals.credit_note_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                        <div style={cellBase}>₹ {totals.debit_note_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                        <div style={cellBase}>₹ {totals.payment_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                        <div style={cellBase}>₹ {totals.tds_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                        <div style={{ ...cellBase, color: '#0059B2' }}>₹ {totals.due_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                        <div style={{ ...cellBase, color: totals.actual_due > 0 ? '#dc2626' : '#16a34a' }}>₹ {totals.actual_due.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </DialogContent>
          <DialogActions sx={{ padding: '8px 14px' }}>
            <Button onClick={handleCloseInvoicePopup} size="small" variant="contained" sx={{ textTransform: 'none', fontSize: '12px', backgroundColor: '#1F2B8F', '&:hover': { backgroundColor: '#172275' } }}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Credit/Debit Notes/Payment Popup */}
        <Dialog
          open={notesPopupOpen}
          onClose={handleCloseNotesPopup}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { maxWidth: '600px' } }}
        >
          <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#1F2B8F', color: '#FFFFFF', padding: '10px 16px' }}>
            <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
              {notesType === 'credit' ? 'Credit Notes Details' : notesType === 'debit' ? 'Debit Notes Details' : 'Payment Details'}
            </Typography>
            <IconButton onClick={handleCloseNotesPopup} size="small" style={{ color: '#FFFFFF' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent style={{ padding: '14px' }}>
            {notesData.length === 0 ? (
              <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                No details available
              </Typography>
            ) : (() => {
              const isPayment = notesType === 'payment';
              const gridCols = isPayment ? '1.2fr 110px 1.6fr 1.1fr 1fr' : '1.4fr 1.2fr 1fr';
              const cellBase = { padding: '6px 8px', textAlign: 'right', fontSize: '12.5px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
              const leftCell = { ...cellBase, justifyContent: 'flex-start', textAlign: 'left' };
              const headerCell = { padding: '7px 8px', fontSize: '11px', fontWeight: 700, color: '#fff', textAlign: 'right', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.3px' };
              const total = notesData.reduce(
                (sum, n) => sum + (parseFloat(isPayment ? n.amount : (n.amount || n.credit || n.debit)) || 0),
                0
              );
              return (
                <div style={{ maxHeight: '420px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                  <div style={{ position: 'sticky', top: 0, zIndex: 1, display: 'grid', gridTemplateColumns: gridCols, backgroundColor: '#1F2B8F' }}>
                    {isPayment ? (
                      <>
                        <div style={{ ...headerCell, textAlign: 'left' }}>Reference No</div>
                        <div style={{ ...headerCell, textAlign: 'center' }}>Payment Date</div>
                        <div style={{ ...headerCell, textAlign: 'left' }}>Description</div>
                        <div style={{ ...headerCell, textAlign: 'center' }}>Adjustment</div>
                        <div style={headerCell}>Amount</div>
                      </>
                    ) : (
                      <>
                        <div style={{ ...headerCell, textAlign: 'left' }}>Invoice</div>
                        <div style={{ ...headerCell, textAlign: 'left' }}>{notesType === 'credit' ? 'Credit Note Ref' : 'Debit Note Ref'}</div>
                        <div style={headerCell}>Amount</div>
                      </>
                    )}
                  </div>

                  {notesData.map((row, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: gridCols,
                        borderBottom: '1px solid #f1f5f9',
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa'
                      }}
                    >
                      {isPayment ? (
                        <>
                          <div style={leftCell}>{row.reference_no || '-'}</div>
                          <div style={{ ...cellBase, justifyContent: 'center', textAlign: 'center' }}>
                            {row.payment_date ? new Date(row.payment_date).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                          </div>
                          <div style={{ ...leftCell }} title={row.description || ''}>
                            {row.description || '-'}
                          </div>
                          {(() => {
                            const refs = adjustmentIndex[String(row.reference_no ?? '').trim()] || [];
                            const count = refs.length;
                            const multiple = count > 1;
                            return (
                              <div
                                style={{
                                  ...cellBase,
                                  justifyContent: 'center',
                                  textAlign: 'center',
                                  color: multiple ? '#b45309' : '#475569',
                                  fontWeight: multiple ? 700 : 400,
                                }}
                                title={count ? `Adjusted against: ${refs.join(', ')}` : ''}
                              >
                                {count === 0 ? '-' : multiple ? `Multiple (${count})` : 'Single'}
                              </div>
                            );
                          })()}
                          <div style={{ ...cellBase, fontWeight: 600, color: '#0059B2' }}>
                            ₹ {parseFloat(row.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={leftCell}>{row.invoice_num || row.invoice || '-'}</div>
                          <div style={leftCell}>{row.reference_no || row.ref_no || row.note_ref || '-'}</div>
                          <div style={{ ...cellBase, fontWeight: 600, color: '#0059B2' }}>
                            ₹ {parseFloat(row.amount || row.credit || row.debit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </div>
                        </>
                      )}
                    </div>
                  ))}

                  {/* Total Row */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: gridCols,
                    backgroundColor: '#e8f4fd',
                    position: 'sticky',
                    bottom: 0,
                    borderTop: '2px solid #0059B2',
                    fontWeight: 700
                  }}>
                    <div style={{ ...leftCell, gridColumn: isPayment ? 'span 4' : 'span 2', justifyContent: 'flex-start' }}>Total</div>
                    <div style={{ ...cellBase, color: '#0059B2' }}>
                      ₹ {total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
              );
            })()}
          </DialogContent>
          <DialogActions sx={{ padding: '8px 14px' }}>
            <Button onClick={handleCloseNotesPopup} size="small" variant="contained" sx={{ textTransform: 'none', fontSize: '12px', backgroundColor: '#1F2B8F', '&:hover': { backgroundColor: '#172275' } }}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Reason Popup for Disabling Toggle */}
        <Dialog 
          open={reasonPopupOpen} 
          onClose={handleCloseReasonPopup}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#1F2B8F', color: '#FFFFFF' }}>
            Disable Automation - Reason Required
            <IconButton onClick={handleCloseReasonPopup} style={{ color: '#FFFFFF' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent style={{ padding: '20px', marginTop: '20px' }}>
            <Typography variant="body1" style={{ marginBottom: '15px' }}>
              Please provide a reason for disabling the automation:
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Enter reason (letters, numbers, and spaces only)"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setReasonError('');
              }}
              error={!!reasonError}
              helperText={reasonError}
              variant="outlined"
            />
          </DialogContent>
          <DialogActions style={{ padding: '10px 20px' }}>
            <Button onClick={handleCloseReasonPopup} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleReasonSubmit} variant="contained" sx={{ backgroundColor: '#1F2B8F', '&:hover': { backgroundColor: '#172275' } }}>
              Submit
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Popup for Enabling Toggle */}
        <Dialog 
          open={confirmPopupOpen} 
          onClose={handleCloseConfirmPopup}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#1F2B8F', color: '#FFFFFF' }}>
            Enable Automation
            <IconButton onClick={handleCloseConfirmPopup} style={{ color: '#FFFFFF' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent style={{ padding: '20px', marginTop: '20px' }}>
            <Typography variant="body1">
              Do you want to enable the mail notification automation?
            </Typography>
          </DialogContent>
          <DialogActions style={{ padding: '10px 20px' }}>
            <Button onClick={handleCloseConfirmPopup} color="inherit">
              No
            </Button>
            <Button onClick={handleConfirmEnable} variant="contained" sx={{ backgroundColor: '#1F2B8F', '&:hover': { backgroundColor: '#172275' } }}>
              Yes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Detailed Export Progress Popup */}
        <Dialog open={exporting} maxWidth="xs" fullWidth>
          <DialogTitle style={{ backgroundColor: '#1F2B8F', color: '#FFFFFF', padding: '12px 16px' }}>
            <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>Exporting client + invoice data</Typography>
          </DialogTitle>
          <DialogContent style={{ padding: '20px' }}>
            <Typography variant="body2" sx={{ mb: 1.5, color: '#475569' }}>
              Fetching invoices — {exportDone} of {exportTotal} clients
            </Typography>
            <LinearProgress
              variant="determinate"
              value={exportTotal > 0 ? Math.round((exportDone / exportTotal) * 100) : 0}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#94a3b8' }}>
              {exportTotal > 0 ? Math.round((exportDone / exportTotal) * 100) : 0}% · the file will download once complete.
            </Typography>
          </DialogContent>
          <DialogActions style={{ padding: '8px 16px' }}>
            <Button
              onClick={() => {
                exportCancelRef.current = true;
                setExportCancel(true);
              }}
              disabled={exportCancel}
              color="inherit"
              size="small"
            >
              {exportCancel ? 'Cancelling…' : 'Cancel'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success/Error Message Snackbar */}
        <Snackbar
          open={alertOpen} 
          autoHideDuration={3000} 
          onClose={() => setAlertOpen(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert 
            onClose={() => setAlertOpen(false)} 
            severity={alertMessage.includes('success') ? 'success' : 'error'}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {alertMessage}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default PaymentCollections;